import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Breadcrumbs,
  Link,
  Grid as MuiGrid,
  Tabs,
  Tab,
  Divider,
  Stack,
  LinearProgress,
  TextField,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  BarChart as BarChartIcon,
  Save as SaveIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import campaignService, { Campaign, CampaignLog, CampaignAnalytics, CampaignRecipient } from '../../services/campaignService';
import DataChart from '../../components/DataChart';

const Grid = MuiGrid;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const CampaignDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [logs, setLogs] = useState<CampaignLog[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [recipientTotal, setRecipientTotal] = useState(0);
  const [originalRecipientEmails, setOriginalRecipientEmails] = useState<Record<number, string>>({});
  const [pendingEmailEdits, setPendingEmailEdits] = useState<Record<number, string>>({});
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingLoading, setSendingLoading] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [sendingTestWhatsApp, setSendingTestWhatsApp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientRowsPerPage, setRecipientRowsPerPage] = useState(25);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testWhatsAppDialogOpen, setTestWhatsAppDialogOpen] = useState(false);
  const [testWhatsAppPhone, setTestWhatsAppPhone] = useState('');

  const loadRecipients = useCallback(async () => {
    if (!id || campaign?.campaign_type === 'whatsapp') return;
    const cid = parseInt(id, 10);
    try {
      setRecipientLoading(true);
      const recipientsData = await campaignService.getCampaignRecipients(cid, {
        page: recipientPage + 1,
        page_size: recipientRowsPerPage,
      });
      const recipientResults = recipientsData.results || [];
      setRecipients(recipientResults);
      setRecipientTotal(recipientsData.count ?? recipientResults.length);
      setOriginalRecipientEmails((prev) => {
        const next = { ...prev };
        recipientResults.forEach((row) => {
          next[row.reservation_id] = (row.email || '').trim();
        });
        return next;
      });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Failed to load recipients');
    } finally {
      setRecipientLoading(false);
    }
  }, [id, campaign?.campaign_type, recipientPage, recipientRowsPerPage]);

  const loadCampaignData = useCallback(async (silent = false) => {
    if (!id) return;
    const cid = parseInt(id, 10);
    try {
      if (!silent) setLoading(true);
      const [campaignData, logsData, analyticsRes] = await Promise.all([
        campaignService.getCampaign(cid),
        campaignService.getCampaignLogs(cid, { page: page + 1, page_size: rowsPerPage }),
        campaignService.getCampaignAnalytics(cid).catch(() => null),
      ]);
      setCampaign(campaignData);
      setLogs(logsData.results || []);
      setLogTotal(logsData.count ?? logsData.results?.length ?? 0);
      if (analyticsRes?.data) setAnalytics(analyticsRes.data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Failed to load campaign data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, page, rowsPerPage]);

  useEffect(() => {
    loadCampaignData(false);
  }, [loadCampaignData]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  useEffect(() => {
    if (!campaign || (campaign.status !== 'sending' && campaign.status !== 'scheduled')) return;
    const t = window.setInterval(() => {
      loadCampaignData(true);
    }, 3000);
    return () => window.clearInterval(t);
  }, [campaign?.status, loadCampaignData]);

  const sendQueuedMessage = (type: string) => {
    if (type === 'email') {
      return 'Campaign queued — emails are sending in the background.';
    }
    if (type === 'whatsapp') {
      return 'Campaign queued — WhatsApp template sends are in progress in the background.';
    }
    return 'Campaign queued — email and WhatsApp sends are in progress in the background.';
  };

  const handleSendCampaign = async () => {
    if (!campaign || !id) return;
    try {
      setSendingLoading(true);
      const res = await campaignService.sendCampaign(parseInt(id, 10), { send_immediately: true });
      setSuccess(
        (res as { message?: string }).message || sendQueuedMessage(campaign.campaign_type)
      );
      setSendDialogOpen(false);
      loadCampaignData(false);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Failed to send campaign');
    } finally {
      setSendingLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!id) return;
    try {
      await campaignService.deleteCampaign(parseInt(id, 10));
      setSuccess('Campaign deleted successfully');
      navigate('/campaigns');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Failed to delete campaign');
      setDeleteDialogOpen(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const getCampaignTypeColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'primary';
      case 'whatsapp':
        return 'success';
      case 'both':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'sending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'scheduled':
        return 'info';
      default:
        return 'default';
    }
  };

  const getLogStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'failed':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <ScheduleIcon color="disabled" fontSize="small" />;
    }
  };

  const getSuccessRate = () => {
    if (!campaign || campaign.total_recipients === 0) return 0;
    return Math.round((campaign.successful_sends / campaign.total_recipients) * 100);
  };

  const updateRecipientEmail = (reservationId: number, value: string) => {
    setPendingEmailEdits((prev) => ({ ...prev, [reservationId]: value }));
    setRecipients((prev) => prev.map((item) => (
      item.reservation_id === reservationId ? { ...item, email: value } : item
    )));
  };

  const getRecipientEmail = (row: CampaignRecipient) => {
    if (pendingEmailEdits[row.reservation_id] !== undefined) {
      return pendingEmailEdits[row.reservation_id];
    }
    return row.email || '';
  };

  const hasRecipientChanges = Object.entries(pendingEmailEdits).some(([reservationId, email]) => {
    const idNum = Number(reservationId);
    const original = originalRecipientEmails[idNum] || '';
    return email.trim() !== original;
  });

  const handleSaveRecipientEmails = async () => {
    if (!id || !hasRecipientChanges) return;
    try {
      setSavingRecipients(true);
      const changedRows = Object.entries(pendingEmailEdits)
        .filter(([reservationId, email]) => {
          const original = originalRecipientEmails[Number(reservationId)] || '';
          return email.trim() !== original;
        })
        .map(([reservationId, email]) => ({
          reservation_id: Number(reservationId),
          email: email.trim() || null,
        }));

      const res = await campaignService.updateCampaignRecipients(parseInt(id, 10), changedRows);
      setPendingEmailEdits({});
      setSuccess(res.message || 'Recipient emails updated successfully');
      loadRecipients();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Failed to update recipient emails');
    } finally {
      setSavingRecipients(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!id || !testEmailAddress.trim()) {
      setError('Enter a test email address');
      return;
    }
    try {
      setSendingTestEmail(true);
      const res = await campaignService.sendTestEmail(parseInt(id, 10), testEmailAddress.trim());
      setSuccess(res.message || 'Test email sent');
      setTestEmailDialogOpen(false);
      setTestEmailAddress('');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSendTestWhatsApp = async () => {
    if (!id || !testWhatsAppPhone.trim()) {
      setError('Enter a test phone number');
      return;
    }
    try {
      setSendingTestWhatsApp(true);
      const res = await campaignService.sendTestWhatsApp(parseInt(id, 10), testWhatsAppPhone.trim());
      setSuccess(res.message || 'Test WhatsApp sent');
      setTestWhatsAppDialogOpen(false);
      setTestWhatsAppPhone('');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Failed to send test WhatsApp');
    } finally {
      setSendingTestWhatsApp(false);
    }
  };

  const groupName = campaign?.group && typeof campaign.group === 'object' ? campaign.group.name : '';
  const groupMembers =
    campaign?.group && typeof campaign.group === 'object' ? campaign.group.member_count : 0;

  if (loading && !campaign) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Campaign not found</Alert>
      </Box>
    );
  }

  const propLabels = analytics
    ? Object.keys(analytics.property_breakdown)
    : [];
  const propSeries = analytics
    ? propLabels.map((k) => analytics.property_breakdown[k])
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" onClick={() => navigate('/campaigns')} sx={{ cursor: 'pointer' }}>
          Campaigns
        </Link>
        <Typography color="text.primary">{campaign.name}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h4" component="h1">
              {campaign.name}
            </Typography>
            <Chip label={campaign.campaign_type} color={getCampaignTypeColor(campaign.campaign_type)} size="small" />
            <Chip label={campaign.status} color={getStatusColor(campaign.status)} size="small" />
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {campaign.description}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Created {formatDate(campaign.created_at)} • Target group: {groupName} ({groupMembers} members)
          </Typography>
          {campaign.send_celery_task_id && (
            <Typography variant="caption" display="block" color="text.secondary">
              Task: {campaign.send_celery_task_id}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/campaigns')}>
            Back
          </Button>
          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
            <Button
              variant="contained"
              startIcon={sendingLoading ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={() => setSendDialogOpen(true)}
              disabled={sendingLoading}
            >
              Send now
            </Button>
          )}
          {campaign.campaign_type !== 'whatsapp' && campaign.email_template && (
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setTestEmailDialogOpen(true)}
            >
              Send test email
            </Button>
          )}
          {campaign.campaign_type !== 'email' && campaign.whatsapp_template && (
            <Button
              variant="outlined"
              startIcon={<ChatIcon />}
              onClick={() => setTestWhatsAppDialogOpen(true)}
            >
              Send test WhatsApp
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadCampaignData(false)}>
            Refresh
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}>
            Delete
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid style={{ gridColumn: 'span 3' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {campaign.total_recipients || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total recipients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid style={{ gridColumn: 'span 3' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {campaign.successful_sends || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Successful sends
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid style={{ gridColumn: 'span 3' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {campaign.failed_sends || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed sends
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid style={{ gridColumn: 'span 3' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {getSuccessRate()}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Success rate
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getSuccessRate()}
                sx={{ mt: 1 }}
                color={getSuccessRate() > 80 ? 'success' : getSuccessRate() > 60 ? 'warning' : 'error'}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab icon={<EmailIcon />} label="Details" />
          <Tab icon={<BarChartIcon />} label="Reports" />
          <Tab icon={<EmailIcon />} label="Send logs" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid style={{ gridColumn: 'span 6' }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Campaign information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="primary">
                      Target group
                    </Typography>
                    <Typography variant="body2">
                      {groupName} ({groupMembers} members)
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="primary">
                      Type
                    </Typography>
                    <Chip label={campaign.campaign_type.toUpperCase()} size="small" />
                  </Box>
                  {campaign.sent_at && (
                    <Box>
                      <Typography variant="subtitle2" color="primary">
                        Sent at
                      </Typography>
                      <Typography variant="body2">{formatDate(campaign.sent_at)}</Typography>
                    </Box>
                  )}
                  {campaign.scheduled_at && (
                    <Box>
                      <Typography variant="subtitle2" color="primary">
                        Scheduled for
                      </Typography>
                      <Typography variant="body2">{formatDate(campaign.scheduled_at)}</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid style={{ gridColumn: 'span 6' }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Template
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {campaign.campaign_type !== 'whatsapp' && campaign.email_template
                  && typeof campaign.email_template === 'object' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="primary">
                      Email
                    </Typography>
                    <Typography variant="body1">{campaign.email_template.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subject: {campaign.email_template.subject}
                    </Typography>
                  </Box>
                )}
                {campaign.campaign_type !== 'email' && campaign.whatsapp_template
                  && typeof campaign.whatsapp_template === 'object' && (
                  <Box>
                    <Typography variant="subtitle2" color="primary">
                      WhatsApp (Meta)
                    </Typography>
                    <Typography variant="body1">{campaign.whatsapp_template.name}</Typography>
                    {campaign.whatsapp_template.meta_template_name && (
                      <Typography variant="body2" color="text.secondary">
                        Meta template: {campaign.whatsapp_template.meta_template_name} (
                        {campaign.whatsapp_template.language_code || 'en'})
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          {campaign.campaign_type !== 'whatsapp' && (
            <Grid style={{ gridColumn: 'span 12' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">
                      Email recipients ({recipientTotal})
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={savingRecipients ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleSaveRecipientEmails}
                      disabled={!hasRecipientChanges || savingRecipients}
                    >
                      Save recipient emails
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {recipientLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : recipients.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No recipients found for this campaign.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Guest</TableCell>
                            <TableCell>Property</TableCell>
                            <TableCell>Email</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recipients.map((recipient) => (
                            <TableRow key={recipient.reservation_id}>
                              <TableCell>{recipient.guest_name || '—'}</TableCell>
                              <TableCell>{recipient.property_name || '—'}</TableCell>
                              <TableCell sx={{ minWidth: 320 }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  type="email"
                                  placeholder="Enter recipient email"
                                  value={getRecipientEmail(recipient)}
                                  onChange={(e) => updateRecipientEmail(recipient.reservation_id, e.target.value)}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    count={recipientTotal}
                    rowsPerPage={recipientRowsPerPage}
                    page={recipientPage}
                    onPageChange={(_, p) => setRecipientPage(p)}
                    onRowsPerPageChange={(e) => {
                      setRecipientRowsPerPage(parseInt(e.target.value, 10));
                      setRecipientPage(0);
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {analytics && (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid style={{ gridColumn: 'span 3' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{analytics.sent_count}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sent (logs)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid style={{ gridColumn: 'span 3' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{analytics.failed_count}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Failed (logs)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid style={{ gridColumn: 'span 3' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{analytics.success_rate}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Success rate (logs)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid style={{ gridColumn: 'span 3' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{analytics.total_logs}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total log rows
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            {propLabels.length > 0 && (
              <DataChart
                title="Sends by property"
                type="bar"
                series={[{ name: 'Messages', data: propSeries }]}
                xaxis={{ categories: propLabels }}
                height={320}
              />
            )}
            {Object.keys(analytics.location_breakdown).length > 0 && (
              <DataChart
                title="Sends by guest country"
                type="donut"
                series={Object.values(analytics.location_breakdown)}
                labels={Object.keys(analytics.location_breakdown)}
                height={300}
              />
            )}
          </Stack>
        )}
        {!analytics && <Alert severity="info">No analytics yet — send the campaign to generate reports.</Alert>}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Sent at</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No send logs yet
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getLogStatusIcon(log.status)}
                        <Chip label={log.status} size="small" color={log.status === 'sent' ? 'success' : 'error'} />
                      </Box>
                    </TableCell>
                    <TableCell>{log.recipient}</TableCell>
                    <TableCell>{log.guest_name}</TableCell>
                    <TableCell>{log.property_name || '—'}</TableCell>
                    <TableCell>{log.guest_country || '—'}</TableCell>
                    <TableCell>{formatDate(log.sent_at)}</TableCell>
                    <TableCell>
                      {log.error_message && (
                        <Tooltip title={log.error_message}>
                          <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 220 }}>
                            {log.error_message}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={logTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TabPanel>

      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
        <DialogTitle>Send campaign</DialogTitle>
        <DialogContent>
          <Typography>
            {campaign.campaign_type === 'email' && (
              <>
                Queue &quot;{campaign.name}&quot; for email delivery to eligible guests?
              </>
            )}
            {campaign.campaign_type === 'whatsapp' && (
              <>
                Queue &quot;{campaign.name}&quot; for approved WhatsApp template delivery via Meta Cloud API
                to eligible guests?
              </>
            )}
            {campaign.campaign_type === 'both' && (
              <>
                Queue &quot;{campaign.name}&quot; to send email and then WhatsApp template messages to
                eligible guests?
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendCampaign}
            color="primary"
            variant="contained"
            disabled={sendingLoading}
            startIcon={sendingLoading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingLoading ? 'Queueing…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={testEmailDialogOpen} onClose={() => setTestEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send test email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends the campaign email template once to the address below. Sample guest data from the
            first recipient is used for template variables.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Test email address"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            placeholder="you@example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestEmailDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendTestEmail}
            color="primary"
            variant="contained"
            disabled={sendingTestEmail || !testEmailAddress.trim()}
            startIcon={sendingTestEmail ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {sendingTestEmail ? 'Sending…' : 'Send test'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={testWhatsAppDialogOpen} onClose={() => setTestWhatsAppDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send test WhatsApp</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends the campaign WhatsApp template once to the number below. Sample guest data from the
            first recipient is used for template variables.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="tel"
            label="Test phone number"
            value={testWhatsAppPhone}
            onChange={(e) => setTestWhatsAppPhone(e.target.value)}
            placeholder="e.g. 9876543210 or +919876543210"
            helperText="Include country code or use a local 10-digit number if configured"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestWhatsAppDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendTestWhatsApp}
            color="primary"
            variant="contained"
            disabled={sendingTestWhatsApp || !testWhatsAppPhone.trim()}
            startIcon={sendingTestWhatsApp ? <CircularProgress size={20} /> : <ChatIcon />}
          >
            {sendingTestWhatsApp ? 'Sending…' : 'Send test'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete campaign</DialogTitle>
        <DialogContent>
          <Typography>Delete &quot;{campaign.name}&quot;? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCampaign} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert severity="success">{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignDetails;
